/**
 * Action Rail Pane
 * Right pane for next steps actions with primary and secondary action buttons
 */

import type { JSX } from 'react'
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Button,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
} from '@fluentui/react-components'
import {
  BookQuestionMarkRegular,
  WrenchScrewdriverRegular,
  PersonQuestionMarkRegular,
  DismissCircleRegular,
  PeopleSwapRegular,
  ClockRegular,
  CheckmarkCircleRegular,
} from '@fluentui/react-icons'
import { EmptyStateMessage } from './EmptyStateMessage'
import { usePaneStyles } from '../styles/usePaneStyles'

const useStyles = makeStyles({
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase400,
    margin: 0,
  },
  body: {
    flex: 1,
    overflow: 'auto',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  primaryActionButton: {
    width: '100%',
    justifyContent: 'flex-start',
    minHeight: '44px',
  },
  moreActionsAccordion: {
    marginTop: tokens.spacingVerticalM,
  },
  secondaryActionButton: {
    width: '100%',
    justifyContent: 'flex-start',
  },
  moreActionsPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalS,
  },
  // Colored icon backgrounds
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusMedium,
  },
  iconRed: {
    backgroundColor: tokens.colorPaletteRedBackground2,
  },
  iconAmber: {
    backgroundColor: tokens.colorPaletteMarigoldBackground2,
  },
  iconGreen: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  iconBlue: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
  },
})

type IconColorVariant = 'red' | 'amber' | 'green' | 'blue' | 'neutral'

interface NextStepAction {
  id: string
  label: string
  icon: JSX.Element
  colorVariant?: IconColorVariant
}

const PRIMARY_ACTIONS: NextStepAction[] = [
  { id: 'explain', label: 'Explain It To Me', icon: <BookQuestionMarkRegular /> },
  { id: 'fix-with-me', label: 'Fix It With Me', icon: <WrenchScrewdriverRegular /> },
  { id: 'ask-expert', label: 'Ask An Expert', icon: <PersonQuestionMarkRegular /> },
]

const MORE_ACTIONS: NextStepAction[] = [
  { id: 'not-it', label: 'Not It', icon: <DismissCircleRegular />, colorVariant: 'red' },
  { id: 'reassign', label: 'Reassign To Someone Else', icon: <PeopleSwapRegular />, colorVariant: 'blue' },
  { id: 'extension', label: 'Request Extension', icon: <ClockRegular />, colorVariant: 'amber' },
  { id: 'close', label: 'Mark This Issue Closed', icon: <CheckmarkCircleRegular />, colorVariant: 'green' },
]

export interface ActionRailPaneProps {
  selectedIssueId: string | null
}

export function ActionRailPane({ selectedIssueId }: ActionRailPaneProps) {
  const paneStyles = usePaneStyles()
  const styles = useStyles()

  const handleActionClick = (actionId: string) => {
    // Placeholder for future implementation
    console.log('Action clicked:', actionId)
  }

  const getIconWithBackground = (icon: JSX.Element, colorVariant?: IconColorVariant) => {
    if (!colorVariant || colorVariant === 'neutral') {
      return icon
    }
    const colorClass = {
      red: styles.iconRed,
      amber: styles.iconAmber,
      green: styles.iconGreen,
      blue: styles.iconBlue,
    }[colorVariant]

    return (
      <span className={mergeClasses(styles.iconWrapper, colorClass)}>
        {icon}
      </span>
    )
  }

  // No selection state
  if (!selectedIssueId) {
    return (
      <div className={paneStyles.root} role="region" aria-label="Action Rail">
        <EmptyStateMessage
          title="Select an issue"
          description="Choose an issue from the work queue to see available actions"
        />
      </div>
    )
  }

  // Ready state with static actions
  return (
    <div className={paneStyles.root} role="region" aria-labelledby="action-rail-title">
      <div className={paneStyles.header}>
        <Text as="h2" id="action-rail-title" weight="semibold" size={400} className={styles.title}>
          Next Steps
        </Text>
      </div>
      <div className={styles.body}>
        {/* Primary actions section */}
        <div className={styles.section}>
          {PRIMARY_ACTIONS.map((action) => (
            <Button
              key={action.id}
              icon={action.icon}
              size="large"
              appearance="primary"
              className={styles.primaryActionButton}
              onClick={() => handleActionClick(action.id)}
            >
              {action.label}
            </Button>
          ))}
        </div>

        {/* More Actions accordion */}
        <Accordion className={styles.moreActionsAccordion} collapsible>
          <AccordionItem value="more-actions">
            <AccordionHeader>More Actions</AccordionHeader>
            <AccordionPanel>
              <div className={styles.moreActionsPanel}>
                {MORE_ACTIONS.map((action) => (
                  <Button
                    key={action.id}
                    icon={getIconWithBackground(action.icon, action.colorVariant)}
                    size="medium"
                    appearance="subtle"
                    className={styles.secondaryActionButton}
                    onClick={() => handleActionClick(action.id)}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  )
}
